import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { contentBriefPathes } from '../../../../../../../../../../constants/queryPathes';
import { statusCodes } from '../../../../../../../../../../constants/statusCodes';

import { axiosAPI } from '../../../../../../../../../../utils/axiosAPI';
import { getAxiosHeaders } from '../../../../../../../../../../utils/helpers/getAxiosHeaders';
import { openBasicErrorNotification } from '../../../../../../../../../../utils/helpers/openBasicErrorNotification';

import {
    setBriefVersions,
    setCurrentBriefId,
    setCurrentBriefLoading,
    setPreviewBriefVersion,
    deleteBriefVersion,
    updateBriefVersionName,
} from '../../store/contentBriefsOutline.actions';
import {
    selectBriefVersions,
    selectCurrentBrief,
    selectCurrentBriefLoading,
    selectPreviewBriefVersion,
} from '../../store/contentBriefsOutline.selectors';

import { sortVersionsByDescendingDate } from '../../../../../../../../../common/tiptapEditor/utils/versionHelpers';

const useBriefVersions = () => {
    const dispatch = useDispatch();

    const { contentOptimizerId } = useParams();

    const briefPreviewVersion = useSelector(selectPreviewBriefVersion);
    const currentBrief = useSelector(selectCurrentBrief);
    const briefVersions = useSelector(selectBriefVersions);
    const isLoading = useSelector(selectCurrentBriefLoading);

    const createNewBriefVersion = () => {
        if (contentOptimizerId && !isLoading) {
            dispatch(setCurrentBriefLoading(true));

            return axiosAPI
                .post(
                    `${contentBriefPathes.getListOfContentBriefs}/${contentOptimizerId}/versions`,
                    {
                        extras: currentBrief?.extras,
                    },
                    {
                        ...getAxiosHeaders(),
                    }
                )
                .then((res) => {
                    const resultData = res?.data;
                    const mappedVersion = {
                        ...resultData,
                        progress: currentBrief.progress,
                    };
                    const updatedVersionHistory = [mappedVersion, ...briefVersions];

                    dispatch(setBriefVersions(updatedVersionHistory));

                    return resultData;
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    dispatch(setCurrentBriefLoading(false));
                });
        }
    };

    const fetchBriefVersions = () => {
        if (contentOptimizerId && !isLoading) {
            dispatch(setCurrentBriefLoading(true));

            axiosAPI
                .get(
                    `${contentBriefPathes.getListOfContentBriefs}/${contentOptimizerId}/versions`,
                    {
                        ...getAxiosHeaders(),
                    }
                )
                .then((result) => {
                    if (result?.status === statusCodes.success) {
                        if (result?.data?.length !== 0) {
                            const sortedVersions = sortVersionsByDescendingDate(result.data);
                            dispatch(setBriefVersions(sortedVersions));
                        } else {
                            createNewBriefVersion();
                        }

                        dispatch(setCurrentBriefId(Number.parseInt(contentOptimizerId)));
                    }
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    dispatch(setCurrentBriefLoading(false));
                });
        }
    };

    const handleActivateBriefVersion = (briefVersionId) => {
        if (contentOptimizerId && briefVersionId) {
            dispatch(setCurrentBriefLoading(true));

            axiosAPI
                .patch(
                    `${contentBriefPathes.createContentBrief}/${contentOptimizerId}/versions/${briefVersionId}`,
                    { active: true },
                    { ...getAxiosHeaders() }
                )
                .then(() => {
                    fetchBriefVersions();
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    dispatch(setCurrentBriefLoading(false));
                });
        }
    };

    const handlePreviewBriefVersion = (versionId) => {
        dispatch(setPreviewBriefVersion(versionId && !briefPreviewVersion ? versionId : null));
    };

    const handleUpdateVersionName = (versionId, versionName) => {
        if (contentOptimizerId && versionId) {
            dispatch(setCurrentBriefLoading(true));

            axiosAPI
                .patch(
                    `${contentBriefPathes.createContentBrief}/${contentOptimizerId}/versions/${versionId}`,
                    { name: versionName },
                    { ...getAxiosHeaders() }
                )
                .then(() => {
                    dispatch(
                        updateBriefVersionName({
                            id: versionId,
                            name: versionName,
                        })
                    );
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    dispatch(setCurrentBriefLoading(false));
                });
        }
    };

    const handleDeleteBriefVersion = (versionId) => {
        if (contentOptimizerId && versionId) {
            dispatch(setCurrentBriefLoading(true));

            axiosAPI
                .delete(
                    `${contentBriefPathes.deleteContentBrief}/${contentOptimizerId}/versions/${versionId}`,
                    { ...getAxiosHeaders() }
                )
                .then(() => {
                    dispatch(deleteBriefVersion(versionId));
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    dispatch(setCurrentBriefLoading(false));
                });
        }
    };

    return {
        briefPreviewVersion,
        isLoading,
        createNewBriefVersion,
        fetchBriefVersions,
        handleActivateBriefVersion,
        handleDeleteBriefVersion,
        handlePreviewBriefVersion,
        handleUpdateVersionName,
    };
};

export default useBriefVersions;
